import { motion } from 'framer-motion';
import { MessageCircle, Github, Linkedin, Instagram, Download } from 'lucide-react';
import { Video } from 'lucide-react';
import { SITE_CONFIG, SOCIAL_LINKS, TECH_STACK, AI_TOOLS } from '../../lib/constants';
import IDCard3D from '../shared/IDCard3D';
import Marquee from '../shared/Marquee';
import { useEffect, useRef } from 'react';
import gsap from 'gsap';

export default function HeroSection() {
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (titleRef.current) {
      const text = titleRef.current.textContent || '';
      titleRef.current.innerHTML = '';
      
      text.split('').forEach((char, i) => {
        const span = document.createElement('span');
        span.textContent = char;
        span.style.opacity = '0';
        titleRef.current?.appendChild(span);
        
        gsap.to(span, {
          opacity: 1,
          duration: 0.05,
          delay: i * 0.05,
        });
      });
    }
  }, []);

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
      {/* Animated Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-neon-blue/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-neon-green/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-block"
            >
              <div className="glass-card px-4 py-2 inline-block">
                <span className="text-neon-green text-sm font-semibold">Developer & AI Specialist</span>
              </div>
            </motion.div>

            <h1 ref={titleRef} className="text-5xl md:text-6xl font-bold text-white leading-tight">
              {SITE_CONFIG.name}
            </h1>

            <p className="text-xl text-neon-blue font-semibold">
              {SITE_CONFIG.title}
            </p>

            <p className="text-2xl text-neon-green italic">
              "{SITE_CONFIG.tagline}"
            </p>

            <p className="text-gray-300 text-lg">
              {SITE_CONFIG.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={SOCIAL_LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-neon-green flex items-center gap-2"
              >
                <MessageCircle size={20} />
                Hubungi via WhatsApp
              </motion.a>

              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="#contact"
                className="btn-neon flex items-center gap-2"
              >
                <Download size={20} />
                Download CV
              </motion.a>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 pt-4">
              <a href={SOCIAL_LINKS.github} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-neon-blue transition">
                <Github size={24} />
              </a>
              <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-neon-blue transition">
                <Linkedin size={24} />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-neon-blue transition">
                <Instagram size={24} />
              </a>
              <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-neon-blue transition">
                <Video size={24} />
              </a>
            </div>
          </motion.div>

          {/* Right - ID Card 3D */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <IDCard3D />
          </motion.div>
        </div>

        {/* Marquee Section */}
        <div className="mt-16 space-y-4">
          <Marquee items={TECH_STACK} direction="left" speed={40} />
          <Marquee items={AI_TOOLS} direction="right" speed={50} />
        </div>
      </div>
    </section>
  );
}
